import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { BpTable } from '../../../../shared/components/cards/table/BpTable'
import { TTableResponse } from '../../../../shared/types/TableResponse.type'
import { ColumnsType } from 'antd/lib/table'
import { IBpPaginationProps } from '../../../../shared/components/cards/pagination/BpPagination'
import { PositionTrackerFilterInterface } from '../../model/types/PositionTrackerFilter.interface'
import {
  changeChartIds,
  selectPositionTrackerChart,
  selectPositionTrackerFilters,
  setSerpPresence,
} from '../../model/positionTrackerSlice'
import { SorterResult, TableRowSelection } from 'antd/lib/table/interface'
import { useSorter } from '../../../../shared/lib/hooks/UseSorter'
import { ExportDrop } from '../../../../shared/components/cards/export-drop/ExportDrop'
import { Switch } from 'antd'
import { PositionTrackerChartInterface } from '../../model/types/PositionTrackerChart.interface'
import { useTranslation } from 'react-i18next'
import {
  CommaLists,
  GetResearchSeoPositionApiArg,
  SeoPositionItems,
  useExportSeoPosition,
  useGetResearchSeoPositionQuery,
} from '../../../../app/api'
import { AiButton } from '../../../../shared/components/ai/ai-button/AiButton'
import { PageCell } from '../../ui/cells/PageCell'
import { LastPositionCell } from '../../ui/cells/LastPositionCell'
import { useAiChat } from '../../../../shared/components/ai/ai-provider/AiProvider'
import { SerpCell } from '../../../../shared/ui/cells/SerpCell'
import clsx from 'clsx'
import styles from './PositionTrackerTable.module.scss'

const MAX_CHECKED_ROWS = 5

export const PositionTrackerTable = () => {
  const { t } = useTranslation()
  const { handleSendMessage } = useAiChat()
  const [countRows, setCountRows] = useState<number>(0)
  const positionTrackerFilter = useAppSelector<PositionTrackerFilterInterface>(
    selectPositionTrackerFilters,
  )
  const { chartIds } = useAppSelector<PositionTrackerChartInterface>(selectPositionTrackerChart)
  const { date, project, engine_id, country, site, keyword, no_serp_presence, landing } =
    positionTrackerFilter

  const [queryParams, setQueryParams] =
    useState<Partial<CommaLists<GetResearchSeoPositionApiArg>>>()
  const { data, isLoading, isFetching } = useGetResearchSeoPositionQuery(
    queryParams as GetResearchSeoPositionApiArg,
    {
      skip:
        !queryParams?.projectId ||
        !queryParams.ownDomain ||
        !queryParams?.countryId ||
        !queryParams.engineId,
    },
  )
  const [exportPositionTracker, { isFetching: exportLoading }] = useExportSeoPosition()
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!project) return

    setQueryParams({
      page: 1,
      'per-page': 10,
      date,
      projectId: +project,
      engineId: +engine_id,
      countryId: country ? +country : undefined,
      ownDomain: site,
      keywordId: keyword || undefined,
      noSerpPresence: no_serp_presence,
      landingId: landing || undefined,
    })
  }, [positionTrackerFilter])

  useEffect(() => {
    if (!data?.data) return
    setCountRows(data.data.total)

    dispatch(changeChartIds((data.data.items ?? []).slice(0, 5).map((item) => item.keyword_id)))
  }, [data])

  const columns: ColumnsType<SeoPositionItems> = [
    {
      title: t('table.columns.keyword'),
      dataIndex: 'keyword',
      key: 'keyword',
    },
    {
      title: t('table.columns.page'),
      dataIndex: 'page',
      key: 'page',
      render: (_: string, record) => <PageCell {...record} />,
    },
    {
      title: t('table.columns.lastPosition'),
      dataIndex: 'position',
      key: 'position',
      render: (_: string, record) => <LastPositionCell {...record} />,
      sorter: true,
    },
    {
      title: t('table.columns.SERP'),
      dataIndex: 'search_screen_url',
      key: 'search_screen_url',
      render: (_: string, record) => <SerpCell {...record} />,
    },
    {
      title: t('table.columns.lastUpdated'),
      dataIndex: 'last_updated',
      key: 'last_updated',
      width: 150,
      sorter: true,
    },
  ]

  const rowSelection: TableRowSelection<SeoPositionItems> = {
    selectedRowKeys: chartIds,
    hideSelectAll: true,
    onSelect: (record, selected) => {
      if (selected) {
        !chartIds?.includes(record.keyword_id) &&
          dispatch(
            changeChartIds(chartIds ? [...chartIds, record.keyword_id] : [record.keyword_id]),
          )
      } else {
        dispatch(changeChartIds(chartIds?.filter((row) => row !== record.keyword_id)))
      }
    },
    getCheckboxProps: (record) => {
      return {
        disabled:
          (chartIds &&
            !chartIds?.includes(record.keyword_id) &&
            chartIds?.length >= MAX_CHECKED_ROWS) ||
          (chartIds?.length === 1 && chartIds?.includes(record.keyword_id)),
      }
    },
  }

  const handleTableChange = (
    { current, pageSize }: IBpPaginationProps,
    sorter?: SorterResult<SeoPositionItems> | SorterResult<SeoPositionItems>[],
  ) => {
    if (queryParams) {
      setQueryParams({
        ...queryParams,
        page: current,
        'per-page': pageSize,
        sort: useSorter(sorter),
      })
    }
  }

  return (
    <section className={clsx(styles.root)}>
      <div className={clsx(styles.header)}>
        <h3 className={clsx(styles.title)}>
          {t('pages.positionTracker.allKeywords')}: {countRows}
        </h3>
        <div className={clsx(styles.actions)}>
          {
            {
              bluepear: (
                <AiButton
                  unstyled
                  onClick={() =>
                    handleSendMessage({
                      message:
                        'Please provide a clear guide on how to use the SEO Position Tracker and explain its value.',
                    })
                  }
                >
                  How the report works
                </AiButton>
              ),
              robuscan: null,
              default: null,
            }[process.env.REACT_APP_PROJECT ?? 'default']
          }
          <span className={clsx(styles.hasBrandRow)}>
            {t('fields.noSERPPresence')}
            <Switch
              checked={queryParams?.noSerpPresence}
              onClick={() => dispatch(setSerpPresence(!queryParams?.noSerpPresence))}
            ></Switch>
          </span>
          <span className={clsx(styles.buttonsRow)}>
            <ExportDrop
              columns={[
                { keys: ['keyword'], label: t('table.columns.keyword') },
                { keys: ['page'], label: t('table.columns.page') },
                { keys: ['position'], label: t('table.columns.lastPosition') },
                { keys: ['previous_position'], label: t('table.columns.previousPosition') },
                { keys: ['search_screen_url'], label: t('table.columns.SERP') },
                { keys: ['last_updated'], label: t('table.columns.lastUpdated') },
              ]}
              loading={exportLoading}
              onExport={(exportParams) =>
                exportPositionTracker({ ...queryParams, ...exportParams })
              }
            />
          </span>
        </div>
      </div>
      <BpTable
        columns={columns}
        data={data as TTableResponse<SeoPositionItems>}
        dataSource={data?.data?.items}
        loading={isLoading || isFetching}
        rowKey={(record) => record.keyword_id}
        rowSelection={rowSelection}
        // над таблицей заголовок с margin-bottom 16px
        stubGap={16}
        onChange={(pagination, filters, sorter) => handleTableChange(pagination, sorter)}
      />
    </section>
  )
}
